import GraphQLQueries from "./queries";
class WordPressGraphQLClient extends GraphQLQueries {
    static BASE_DOMAIN = "https://dervisoksuzoglu.xyz";
    static GRAPHQL_ENDPOINT = "https://dervisoksuzoglu.xyz/wpb/graphql";
    static async executeQuery(query, variables) {
        const response = await fetch(this.GRAPHQL_ENDPOINT, {
            method: "POST",
            mode: "cors",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json"
            },
            body: JSON.stringify({
                query,
                variables,
            }),
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        else {
            const result = await response.json();
            if (result.errors && result.errors.length > 0) {
                throw new Error(`GraphQL Error: ${result.errors.map(e => e.message).join(", ")}`);
            }
            else {
                return result;
            }
        }
    }
    static transformPostPreview(node) {
        return {
            id: node.id || "Unknown ID",
            title: node.title || "Unknown Title"
        };
    }
    static transformPostNode(node) {
        const postUrl = this.generatePostUrl(node.slug);
        if (!node) {
            throw new Error(`<b>Post not found.</b>`);
        }
        else {
            return {
                id: node.id,
                title: node.title || "Unknown Title",
                date: node.date || "Date Unknown",
                categories: node.categories.nodes,
                content: node.content || "<p>No proper content.</p>",
                author: {
                    name: node.author.node.name || "Unknown Author",
                },
                url: postUrl
            };
        }
    }
    static DEF_BLOG_POST_NUMBER_TO_BE_FETCHED = 10;
    static CACHE_KEY_PREVIEW = "wordpress_blog_post_previews";
    static CACHE_KEY_POST_PREFIX = "wordpress_blog_post_";
    static CACHE_EXPIRATION_HOURS = 24;
    static async fetchBlogPostPreviews(variables = { first: WordPressGraphQLClient.DEF_BLOG_POST_NUMBER_TO_BE_FETCHED }) {
        const cachedData = this.getCachedPreviews();
        if (cachedData && cachedData.length > 0) {
            return cachedData;
        }
        try {
            const response = await this.executeQuery(this.GRAPHQL_QUERY_FETCH_POST_PREVIEWS, variables);
            const transformedPreviews = response.data.posts.nodes.map(this.transformPostPreview);
            this.cachePreviews(transformedPreviews);
            return transformedPreviews;
        }
        catch (error) {
            throw new Error("Error fetching WordPress post previews: " + error);
        }
    }
    static async fetchSinglePost(postId) {
        const cachedPost = this.getCachedPost(postId);
        if (cachedPost) {
            return cachedPost;
        }
        try {
            const response = await this.executeQuery(this.GRAPHQL_QUERY_FETCH_SINGLE_POST, { id: postId, });
            if (!response.data.post) {
                throw new Error("No post found with the given ID");
            }
            const transformedPost = this.transformPostNode(response.data.post);
            this.cachePost(transformedPost);
            return transformedPost;
        }
        catch (error) {
            throw new Error("Error fetching WordPress post: " + error);
        }
    }
    static async fetchPostBySlug(slug) {
        const cachedPost = this.getCachedPostBySlug(slug);
        if (cachedPost) {
            return cachedPost;
        }
        else {
            try {
                const response = await this.executeQuery(this.GRAPHQL_QUERY_FETCH_POST_BY_SLUG, { slug });
                if (!response.data.postBy) {
                    throw new Error("No post found with the given slug");
                }
                const transformedPost = this.transformPostNode(response.data.postBy);
                this.cachePost(transformedPost);
                return transformedPost;
            }
            catch (error) {
                throw new Error("Error fetching WordPress post by slug: " + error);
            }
        }
    }
    static getCachedPreviews() {
        try {
            const cachedItem = localStorage.getItem(WordPressGraphQLClient.CACHE_KEY_PREVIEW);
            if (!cachedItem) {
                return [];
            }
            const parsedCache = JSON.parse(cachedItem);
            const currentTime = new Date().getTime();
            const cacheAge = (currentTime - parsedCache.timestamp) / (1000 * 60 * 60);
            if (cacheAge < this.CACHE_EXPIRATION_HOURS) {
                return parsedCache.previews;
            }
            localStorage.removeItem(WordPressGraphQLClient.CACHE_KEY_PREVIEW);
            return [];
        }
        catch (error) {
            console.error("Error retrieving cached previews:", error);
            return [];
        }
    }
    static cachePreviews(previews) {
        try {
            const cacheItem = {
                timestamp: new Date().getTime(),
                previews: previews
            };
            localStorage.setItem(WordPressGraphQLClient.CACHE_KEY_PREVIEW, JSON.stringify(cacheItem));
        }
        catch (error) {
            console.error('Error caching previews in localStorage:', error);
        }
    }
    static getCachedPost(postId) {
        try {
            const cacheKey = this.CACHE_KEY_POST_PREFIX + postId;
            const cachedItem = localStorage.getItem(cacheKey);
            if (!cachedItem) {
                return null;
            }
            const parsedCache = JSON.parse(cachedItem);
            const currentTime = new Date().getTime();
            const cacheAge = (currentTime - parsedCache.timestamp) / (1000 * 60 * 60);
            if (cacheAge < this.CACHE_EXPIRATION_HOURS) {
                return parsedCache.post;
            }
            localStorage.removeItem(cacheKey);
            return null;
        }
        catch (error) {
            console.error("Error retrieving cached post:", error);
            return null;
        }
    }
    static getCachedPostBySlug(slug) {
        try {
            const cacheKey = `${this.CACHE_KEY_POST_PREFIX}slug_${slug}`;
            const cachedItem = localStorage.getItem(cacheKey);
            if (!cachedItem) {
                return null;
            }
            const parsedCache = JSON.parse(cachedItem);
            const currentTime = new Date().getTime();
            const cacheAge = (currentTime - parsedCache.timestamp) / (1000 * 60 * 60);
            if (cacheAge < this.CACHE_EXPIRATION_HOURS) {
                return parsedCache.post;
            }
            localStorage.removeItem(cacheKey);
            return null;
        }
        catch (error) {
            console.error("Error retrieving cached post by slug:", error);
            return null;
        }
    }
    static cachePost(post) {
        try {
            const cacheKey = this.CACHE_KEY_POST_PREFIX + post.id;
            const cacheItem = {
                timestamp: new Date().getTime(),
                post: post
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
        }
        catch (error) {
            console.error('Error caching post in localStorage:', error);
        }
    }
    static generatePostUrl(slug) {
        const newDomain = WordPressGraphQLClient.BASE_DOMAIN;
        if (!newDomain) {
            throw new Error(`Unable to localte ${newDomain}`);
        }
        else {
            return `${newDomain}/${slug}`;
        }
    }
}
export default WordPressGraphQLClient;
